
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import AppLayout from '@/components/feature/AppLayout';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;
const OFFICE_API =
  'https://guardiancloudcare.com/backend/api/get_supervisor_offices.php';

interface Office {
  uid: string;
  name: string;
  supervisor_name: string;
  municipality: string | null;
  province: string | null;
  latitude: number;
  longitude: number;
}

interface OfficeResponse {
  success: boolean;
  offices: Office[];
  error?: string;
}

export default function VDashboardPage() {
  const navigate = useNavigate();
  const mapContainer = useRef<HTMLDivElement>(null);

  const [officeCount, setOfficeCount] = useState(0);
  const [mapError, setMapError] = useState('');
  const [loadingOffices, setLoadingOffices] = useState(true);

  useEffect(() => {
    if (!mapContainer.current) return;

    if (!MAPBOX_TOKEN) {
      setMapError(
        'Mapbox token is missing. Check your VITE_MAPBOX_TOKEN environment variable.'
      );
      setLoadingOffices(false);
      return;
    }

    mapboxgl.accessToken = MAPBOX_TOKEN;

    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [121.45, 14.32],
      zoom: 9,
      attributionControl: true,
    });

    map.addControl(
      new mapboxgl.NavigationControl(),
      'top-right'
    );

    let cancelled = false;
    let markers: mapboxgl.Marker[] = [];

    const loadOffices = async () => {
      try {
        setLoadingOffices(true);
        setMapError('');

        const response = await fetch(OFFICE_API, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
          cache: 'no-store',
        });

        if (!response.ok) {
          throw new Error(
            `Unable to load office locations (HTTP ${response.status}).`
          );
        }

        const result: OfficeResponse = await response.json();

        if (
          !result.success ||
          !Array.isArray(result.offices)
        ) {
          throw new Error(
            result.error || 'Invalid office API response.'
          );
        }

        if (cancelled) return;

        // Remove old markers before refreshing the map.
        markers.forEach((marker) => marker.remove());
        markers = [];

        // Only one pin for each unique office coordinate.
        const uniqueOffices = new Map<string, Office>();

        for (const office of result.offices) {
          const latitude = Number(office.latitude);
          const longitude = Number(office.longitude);

          if (
            !Number.isFinite(latitude) ||
            !Number.isFinite(longitude) ||
            latitude < -90 ||
            latitude > 90 ||
            longitude < -180 ||
            longitude > 180
          ) {
            continue;
          }

          const key =
            `${latitude.toFixed(6)},${longitude.toFixed(6)}`;

          if (!uniqueOffices.has(key)) {
            uniqueOffices.set(key, {
              ...office,
              latitude,
              longitude,
            });
          }
        }

        const validOffices = Array.from(
          uniqueOffices.values()
        );

        for (const office of validOffices) {
          if (cancelled) break;

          const popupContent = document.createElement('div');
          popupContent.style.padding = '6px';

          const title = document.createElement('strong');
          title.textContent = office.name || 'DSWD Office';

          const location = document.createElement('p');
          location.style.margin = '6px 0';
          location.textContent = [
            office.municipality,
            office.province,
          ]
            .filter(Boolean)
            .join(', ');

          popupContent.append(title, location);

          const popup = new mapboxgl.Popup({
            offset: 25,
            maxWidth: '280px',
          }).setDOMContent(popupContent);

          const marker = new mapboxgl.Marker({
            color: '#2563eb',
          })
            .setLngLat([
              office.longitude,
              office.latitude,
            ])
            .setPopup(popup)
            .addTo(map);

          markers.push(marker);
        }

        setOfficeCount(validOffices.length);

        // Fit the map around all office pins.
        if (validOffices.length > 0) {
          const bounds = new mapboxgl.LngLatBounds();

          validOffices.forEach((office) => {
            bounds.extend([
              office.longitude,
              office.latitude,
            ]);
          });

          if (validOffices.length === 1) {
            map.flyTo({
              center: [
                validOffices[0].longitude,
                validOffices[0].latitude,
              ],
              zoom: 12,
            });
          } else {
            map.fitBounds(bounds, {
              padding: 60,
              maxZoom: 12,
              duration: 800,
            });
          }
        } else {
          setMapError(
            'No approved supervisor offices with valid coordinates were found.'
          );
        }
      } catch (error) {
        if (!cancelled) {
          console.error(
            'Victim dashboard office map error:',
            error
          );

          setMapError(
            error instanceof Error
              ? error.message
              : 'Unable to load DSWD office locations.'
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingOffices(false);
        }
      }
    };

    const handleMapLoad = () => {
      void loadOffices();
    };

    map.on('load', handleMapLoad);

    // Refresh office locations when the browser tab becomes active.
    const handleVisibilityChange = () => {
      if (!document.hidden && !cancelled) {
        void loadOffices();
      }
    };

    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange
    );

    return () => {
      cancelled = true;

      map.off('load', handleMapLoad);

      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
      );

      markers.forEach((marker) => marker.remove());
      markers = [];

      map.remove();
    };
  }, []);

  return (
    <AppLayout
      title="Victim Dashboard"
      breadcrumb={['Victim', 'Dashboard']}
    >
      <div className="max-w-7xl mx-auto space-y-6">
        <section className="relative overflow-hidden rounded-3xl bg-red-500 p-6 md:p-8 text-white shadow-sm">
          <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/10" />
          <div className="absolute -left-10 -bottom-10 w-32 h-32 rounded-full bg-white/10" />

          <div className="relative z-10">
            <div className="flex items-center gap-2 text-xs font-bold uppercase mb-4">
              <i className="ri-shield-cross-line text-lg" />
              Laguna Emergency Hotline
            </div>

            <h1 className="text-2xl md:text-4xl font-extrabold">
              Report Child Abuse
            </h1>

            <p className="text-sm md:text-base mt-2 text-white/90">
              If a child is in immediate danger, call the Laguna emergency hotline.
            </p>

            <a
              href="tel:09194173698"
              className="mt-6 w-full md:w-auto bg-white text-red-500 font-bold px-8 py-4 rounded-2xl flex items-center justify-center gap-2 hover:bg-red-50 transition"
            >
              <i className="ri-phone-fill" />
              Call 0919-417-3698
            </a>
          </div>
        </section>

        <section className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 flex items-start gap-3">
          <i className="ri-information-line text-yellow-600 mt-0.5" />

          <div className="flex-1">
            <h3 className="font-semibold text-yellow-800">
              Report Anonymously
            </h3>

            <p className="text-sm text-yellow-700 mt-1">
              You can file a report without creating an account.
              Your identity will remain confidential.
            </p>
          </div>
        </section>

        {/* Read-only DSWD office map */}
        <section className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-5 md:p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-gray-800">
                  DSWD Office Locations
                </h2>

                <p className="text-sm text-gray-500 mt-1">
                  View available supervisor offices in Laguna.
                  Select a blue pin to see its office name and location.
                </p>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <span className="h-3 w-3 rounded-full bg-blue-600" />
                <span className="text-xs text-gray-600">
                  DSWD Office
                </span>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
              <i className="ri-map-pin-line text-blue-600 text-lg" />

              {loadingOffices
                ? 'Loading office locations...'
                : `${officeCount} office location(s) displayed`}
            </div>

            <div
              ref={mapContainer}
              className="mt-4 h-[380px] md:h-[460px] w-full overflow-hidden rounded-2xl border border-gray-200"
            />

            {mapError && (
              <div
                role="alert"
                className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
              >
                {mapError}
              </div>
            )}

            <p className="text-xs text-gray-400 mt-3">
              This map is for viewing office locations only.
              It does not collect or submit your location.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-base font-bold text-gray-800 mb-4">
            Quick Actions
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <ActionCard
              icon="ri-alert-line"
              title="Report Incident"
              desc="File a confidential report"
              color="bg-orange-500"
              onClick={() => navigate('/v-report')}
            />

            <ActionCard
              icon="ri-phone-fill"
              title="Emergency Call"
              desc="Call Laguna hotline now"
              color="bg-red-500"
              onClick={() => {
                window.location.href = 'tel:09194173698';
              }}
            />

            <ActionCard
              icon="ri-scales-3-line"
              title="Law"
              desc="View child protection laws"
              color="bg-blue-500"
              onClick={() => navigate('/v-law')}
            />

            <ActionCard
              icon="ri-book-open-line"
              title="Resources"
              desc="Find Laguna support offices"
              color="bg-emerald-500"
              onClick={() => navigate('/v-resources')}
            />
          </div>
        </section>

        <section className="bg-yellow-50 rounded-3xl p-5 md:p-6 flex gap-4 border border-yellow-100">
          <div className="w-12 h-12 rounded-2xl bg-yellow-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-shield-check-line text-yellow-600 text-xl" />
          </div>

          <div>
            <h3 className="font-bold text-gray-800">
              Your Report is Confidential
            </h3>

            <p className="text-sm text-gray-600 mt-2 leading-relaxed">
              All reports are handled by trained MSWD social workers.
              You can choose to remain anonymous. Every report is
              investigated with the utmost care and confidentiality.
            </p>
          </div>
        </section>
      </div>
    </AppLayout>
  );
}

function ActionCard({
  icon,
  title,
  desc,
  color,
  onClick,
}: {
  icon: string;
  title: string;
  desc: string;
  color: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm text-left hover:shadow-md hover:-translate-y-1 transition"
    >
      <div
        className={`w-12 h-12 ${color} rounded-2xl flex items-center justify-center mb-4`}
      >
        <i className={`${icon} text-white text-xl`} />
      </div>

      <h3 className="font-bold text-gray-800">
        {title}
      </h3>

      <p className="text-sm text-gray-400 mt-1">
        {desc}
      </p>
    </button>
  );
}
